<template>
  <tbody :class="{ 'is-expanded': isExpanded }">
  <!-- Main row -->
  <tr class="border-bottom">
    <!-- Email -->
    <td class="col-email py-3 pl-3">
      <div class="d-flex align-items-center">
        <CIcon name="cipRobot" size="custom-size" :height="16" class="mr-2" v-if="isAdvancedUser && collaborator.is_virtual"/>
        <span class="text-truncate d-inline-block">{{ collaborator.email }}</span>
      </div>
    </td>
    <!-- Role badges -->
    <td class="col-roles py-3">
      <div class="d-flex align-items-center flex-wrap">
        <template v-if="roles.length > 0">
          <CBadge
            v-for="role in visibleRoles"
            :key="role.id"
            :color="role.is_inherited ? 'light' : 'primary'"
            :class="['badge-outline mr-2 mb-1 text-left']"
          >
            {{ role.display_name || role.name }}
            <small class="d-block mt-1" v-if="showRealName(role)"> ({{ role.name }})</small>
          </CBadge>
          <span v-if="roles.length > 3">
              +{{ roles.length - 3 }}
            </span>
        </template>
        <span v-else class="text-muted small">
            <translate translate-context="collaborators.row">No roles</translate>
          </span>
      </div>
    </td>

    <!-- Status -->
    <td class="col-status py-3 text-center">
      <CBadge v-if="summaryStatus" :color="summaryStatusColor" class="badge-outline">
        {{ summaryStatusLabel }}
      </CBadge>
    </td>


    <!-- Actions -->
    <td class="col-actions py-3 pr-3 text-right">
      <div class="d-flex justify-content-end align-items-center">
        <CDropdown
          v-if="hasAnyAction"
          color="link"
          size="sm"
          :caret="false"
          placement="bottom-end"
        >
          <template #toggler-content>
            <CIcon name="cipDotsVertical" size="sm" style="color: #4E4D64"/>
          </template>

          <!-- Add role -->
          <CDropdownItem
            v-if="hasAssignableRoles && hasScopePerm('collaborator.add_role')"
            @click="$emit('add-role', collaborator)"
          >
            <CIcon name="cipShield01" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">Add role</translate>
          </CDropdownItem>

          <!-- View permissions -->
          <CDropdownItem
            v-if="hasScopePerm('collaborator.show_user_permissions')"
            @click="$emit('view-permissions', collaborator)"
          >
            <CIcon name="cipEye" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">View permissions</translate>
          </CDropdownItem>

          <!-- User detail (Customer Success) -->
          <CDropdownItem
            v-if="canViewUserDetail"
            @click="goToUserDetail"
          >
            <CIcon name="cipUser" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">User detail</translate>
          </CDropdownItem>

          <!-- FTP Credentials -->
          <CDropdownItem
            v-if="canViewFtpCredentials"
            @click="$emit('view-ftp-credentials', collaborator)"
          >
            <CIcon name="cipUpload" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">FTP credentials</translate>
          </CDropdownItem>

          <CDropdownDivider v-if="hasGeneralActions && hasVirtualActions"/>

          <!-- Virtual: Reset password -->
          <CDropdownItem
            v-if="canResetPassword"
            @click="$emit('reset-password', collaborator)"
          >
            <CIcon name="cipPasscodeLock" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">Reset password</translate>
          </CDropdownItem>

          <CDropdownDivider v-if="(hasGeneralActions || hasVirtualActions) && hasPendingActions"/>

          <!-- Pending non-virtual: Edit email -->
          <CDropdownItem
            v-if="canEditEmail"
            @click="$emit('edit-email', collaborator)"
          >
            <CIcon name="cipPencil" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">Edit email</translate>
          </CDropdownItem>

          <!-- Pending non-virtual: Resend invitation -->
          <CDropdownItem
            v-if="canResendInvitation"
            @click="$emit('resend-invitation', collaborator)"
          >
            <CIcon name="cipSend01" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">Resend invitation</translate>
          </CDropdownItem>

          <!-- Pending: Force accept -->
          <CDropdownItem
            v-if="canForceAccept"
            @click="$emit('force-accept', collaborator)"
          >
            <CIcon name="cipCircleCheck" size="custom-size" :height="16" class="mr-2"/>
            <translate translate-context="collaborators.menu">Force accept</translate>
          </CDropdownItem>

          <!-- Disable -->
          <template v-if="canDisable || canEnable">
            <CDropdownDivider v-if="hasGeneralActions || hasVirtualActions || hasPendingActions"/>
            <CDropdownItem
              v-if="canDisable"
              @click="$emit('disable', collaborator)"
            >
              <CIcon name="cipSlashCircle01" size="custom-size" :height="16" class="mr-2"/>
              <translate translate-context="collaborators.menu">Disable</translate>
            </CDropdownItem>
            <CDropdownItem
              v-if="canEnable"
              @click="$emit('enable', collaborator)"
            >
              <CIcon name="cipCircleCheck" size="custom-size" :height="16" class="mr-2"/>
              <translate translate-context="collaborators.menu">Enable</translate>
            </CDropdownItem>
          </template>

          <!-- Admin actions (global scope only) -->
          <template v-if="hasAdminActions">
            <CDropdownDivider
              v-if="hasGeneralActions || hasVirtualActions || hasPendingActions || canDisable || canEnable"/>

            <!-- Set as Virtual User -->
            <CDropdownItem
              v-if="canSetVirtual"
              @click="$emit('set-virtual', collaborator)"
            >
             <CIcon name="cipRobot" size="custom-size" :height="16" class="mr-2"/>
              <translate translate-context="collaborators.admin">Set as virtual user</translate>
            </CDropdownItem>

            <!-- Set as Regular User -->
            <CDropdownItem
              v-if="canUnsetVirtual"
              @click="$emit('unset-virtual', collaborator)"
            >
              <CIcon name="cipUser" size="custom-size" :height="16" class="mr-2"/>
              <translate translate-context="collaborators.admin">Set as regular user</translate>
            </CDropdownItem>

            <!-- Change Account Email -->
            <CDropdownItem
              v-if="canChangeAccountEmail"
              @click="$emit('change-account-email', collaborator)"
            >
              <CIcon name="cipEmail" size="custom-size" :height="16" class="mr-2"/>
              <translate translate-context="collaborators.admin">Change email</translate>
            </CDropdownItem>
          </template>

          <!-- Delete -->
          <template v-if="canDelete">
            <CDropdownDivider
              v-if="hasGeneralActions || hasVirtualActions || hasPendingActions || canDisable || canEnable || hasAdminActions"/>
            <CDropdownItem @click="$emit('delete', collaborator)">
              <CIcon name="cipTrashFull" size="custom-size" :height="16" class="mr-2 text-danger"/>
              <span class="text-danger">
                <translate translate-context="collaborators.menu">Delete</translate>
              </span>
            </CDropdownItem>
          </template>

        </CDropdown>

        <!-- Chevron toggle -->
        <div v-if="isAdvancedUser"
             class="col-chevron py-3 text-center"
             @click="$emit('toggle-expand', collaborator.id)">
          <CIcon :name="isExpanded ? 'cipChevronDown' : 'cipChevronUp'" size="custom-size" :height="18"/>
        </div>
      </div>


    </td>


  </tr>

  <!-- Expanded section -->
  <tr v-if="isExpanded">
    <td :colspan="isAdvancedUser ? 5 : 4" class="p-0">
      <CollaboratorRoleDetail
        :collaborator="collaborator"
        :available-roles="availableRoles"
        :scope="scope"
        @disable-role="$emit('disable-role', $event)"
        @enable-role="$emit('enable-role', $event)"
        @delete-role="$emit('delete-role', $event)"
        @view-role-permissions="$emit('view-role-permissions', $event)"
      />
    </td>
  </tr>
  </tbody>
</template>

<script>
import CollaboratorStatus from '@/domain/core/constant/collaboratorStatus'
import CollaboratorRoleDetail from './CollaboratorRoleDetail.vue'
import {mapGetters} from "vuex";

export default {
  name: 'CollaboratorRow',

  components: {
    CollaboratorRoleDetail
  },

  props: {
    collaborator: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    },
    scope: {
      type: Object,
      default: () => ({})
    },
    scopeType: {
      type: String,
      required: true
    },
    isExpanded: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters('event', [
      'isSaas'
    ]),
    roles() {
      const all = this.collaborator.roles || []
      // Direct roles first, inherited after
      return [...all].sort((a, b) => (a.is_inherited ? 1 : 0) - (b.is_inherited ? 1 : 0))
    },

    isAdvancedUser() {
      return this.hasPerm('collaborator.advanced')
    },

    visibleRoles() {
      return this.roles.slice(0, 3)
    },

    isAllInherited() {
      return this.directRoles.length === 0 && (this.collaborator.roles || []).length > 0
    },

    hasAssignableRoles() {
      const currentRoleIds = new Set((this.collaborator.roles || []).map(r => r.id))
      return this.availableRoles.some(r => !currentRoleIds.has(r.id))
    },

    canViewUserDetail() {
      return this.collaborator.user_id && this.hasPerm('admin.users.search')
    },

    hasGeneralActions() {
      return (this.hasAssignableRoles && this.hasScopePerm('collaborator.add_role')) ||
        this.hasScopePerm('collaborator.show_user_permissions') ||
        this.canViewUserDetail ||
        this.canViewFtpCredentials
    },

    // Ruoli diretti (non ereditati) per calcolo stato riassuntivo
    directRoles() {
      return (this.collaborator.roles || []).filter(r => !r.is_inherited)
    },

    // Stato riassuntivo: 'active' | 'disabled' | 'inherited' | null (se mix)
    summaryStatus() {
      const allRoles = this.collaborator.roles || []
      if (allRoles.length === 0) return null

      // Se tutti i ruoli sono inherited → inherited
      if (this.directRoles.length === 0) {
        return allRoles.length > 0 ? 'inherited' : null
      }

      // Stato basato sui ruoli diretti
      const allActive = this.directRoles.every(r => r.is_active !== false)
      const allDisabled = this.directRoles.every(r => r.is_active === false)
      if (allActive) return 'active'
      if (allDisabled) return 'disabled'
      return null
    },

    summaryStatusColor() {
      if (this.summaryStatus === 'disabled') return 'secondary'
      if (this.summaryStatus === 'inherited') return 'info'
      return 'success'
    },

    summaryStatusLabel() {
      if (this.summaryStatus === 'disabled') {
        return this.$pgettext('collaborators.row', 'Disabled')
      }
      if (this.summaryStatus === 'inherited') {
        return this.$pgettext('collaborators.row', 'Inherited')
      }
      return this.$pgettext('collaborators.row', 'Active')
    },

    isPending() {
      return this.collaborator.status === CollaboratorStatus.PENDING
    },

    isDisabled() {
      return this.collaborator.status === CollaboratorStatus.DISABLED
    },

    isAccepted() {
      return this.collaborator.status === CollaboratorStatus.ACCEPTED
    },

    canResetPassword() {
      return this.collaborator.is_virtual &&
        !this.isAllInherited &&
        this.hasScopePerm('collaborator.virtual')
    },

    canEditEmail() {
      return this.isPending && !this.collaborator.is_virtual &&
        this.hasScopePerm('collaborator.update_email')
    },

    canResendInvitation() {
      return this.isPending && !this.collaborator.is_virtual &&
        this.hasScopePerm('collaborator.resend_invitation')
    },

    canForceAccept() {
      return this.isPending && this.hasScopePerm('collaborator.assign')
    },

    hasVirtualActions() {
      return this.canResetPassword
    },

    hasPendingActions() {
      return this.canEditEmail || this.canResendInvitation || this.canForceAccept
    },

    hasAdminActions() {
      return this.canSetVirtual || this.canUnsetVirtual || this.canChangeAccountEmail
    },

    canSetVirtual() {
      return !this.collaborator.is_virtual &&
        this.collaborator.user_id &&
        this.isAccepted &&
        this.hasPerm('admin.users.advanced') &&
        this.scopeType === 'global'
    },

    canUnsetVirtual() {
      return this.collaborator.is_virtual &&
        this.collaborator.user_id &&
        this.hasPerm('admin.users.advanced') &&
        this.scopeType === 'global'
    },

    canChangeAccountEmail() {
      return !this.collaborator.is_virtual &&
        this.collaborator.user_id &&
        this.hasPerm('admin.users.advanced') &&
        this.scopeType === 'global'
    },

    canViewFtpCredentials() {
      if (this.scopeType === 'pica-service') {
        return this.hasPerm('collaborator.export_credentials')
      } else if (this.scopeType === 'contract-manager') {
        return this.hasPerm('contract_manager.collaborator.export_credentials')
      }
      return this.hasPerm('admin.collaborator.export_credentials')
    },

    hasAnyAction() {
      return this.hasGeneralActions || this.hasVirtualActions || this.hasPendingActions ||
        this.canDisable || this.canEnable || this.hasAdminActions || this.canDelete
    },

    // Shortcut: disabilita tutti i ruoli diretti attivi
    canDisable() {
      return this.directRoles.some(r => r.is_active !== false) &&
        this.hasScopePerm('collaborator.disable_role')
    },

    // Shortcut: abilita tutti i ruoli diretti disabilitati
    canEnable() {
      return this.directRoles.length > 0 &&
        this.directRoles.every(r => r.is_active === false) &&
        this.hasScopePerm('collaborator.enable_role')
    },

    // Shortcut: elimina tutti i ruoli diretti
    canDelete() {
      return this.directRoles.length > 0 &&
        this.hasScopePerm('collaborator.remove_role')
    }
  },

  methods: {
    showRealName(role) {
      return this.isAdvancedUser && role.display_name && role.display_name !== role.name
    },

    goToUserDetail() {
      const route = this.$router.resolve({
        name: 'customer_success.user',
        params: {id: this.collaborator.user_id}
      })
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style scoped>

.col-status {
  width: 1px;
  white-space: nowrap;
}

.col-actions {
  width: 30px;
  white-space: nowrap;
}


.col-chevron {
  width: 1px;
  white-space: nowrap;
  cursor: pointer;
}

.col-chevron:hover {
  opacity: 0.7;
}

</style>
